/**
 * screens-import.ts — นำเข้าและซิงค์ข้อมูล (T097)
 *
 * ขั้นตอน: เลือกไฟล์ → ชุดข้อมูล (preset) → map คอลัมน์ → ตรวจก่อนนำเข้า → ยืนยัน
 * กติกา:
 * - preview ไม่แตะข้อมูลหลัก; confirm เป็น upsert (ไฟล์เดิมอัปเดตของเดิม ไม่บวกยอดซ้ำ)
 * - แสดง new/updated/unchanged/error/review + ช่วงข้อมูล + เตือนงวดซ้ำก่อนยืนยัน
 * - ย้อน batch ได้พร้อมเหตุผล (audit-safe)
 */
import { IMPORT_PRESETS, presetByKey, guessMapping, presetToConfig } from '@rrp/shared';
import type { Page } from './router.js';
import { getTenant } from './state.js';
import * as api from './api.js';
import { mountTikTok } from './screens-orders.js';
import {
  esc,
  pageShell,
  dataTable,
  pill,
  val,
  opt,
  bindSubmit,
  errorBox,
  drawerHtml,
  showDrawer,
  promptDialog,
} from './ui.js';

type Row = Record<string, unknown>;

const CHANNELS: [string, string][] = [
  ['', 'ไม่ระบุช่องทาง'],
  ['tiktok_shop', 'TikTok Shop'],
  ['shopee', 'Shopee'],
  ['lazada', 'Lazada'],
  ['facebook', 'Facebook'],
  ['line', 'LINE'],
];

/** parse CSV แบบเบา (RFC4180 subset) เพื่อดูหัวตาราง/ตัวอย่างก่อนส่ง API */
export function parseCsvPreview(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length === 0) return { headers: [], rows: [] };
  const split = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const c = line[i]!;
      if (quoted) {
        if (c === '"' && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else if (c === '"') quoted = false;
        else cur += c;
      } else if (c === '"') quoted = true;
      else if (c === ',') {
        out.push(cur.trim());
        cur = '';
      } else cur += c;
    }
    out.push(cur.trim());
    return out;
  };
  const headers = split(lines[0]!);
  return { headers, rows: lines.slice(1, 6).map(split) };
}

const STATUS_LABELS: Record<string, string> = {
  new: 'รายการใหม่',
  updated: 'อัปเดตของเดิม',
  unchanged: 'ไม่เปลี่ยนแปลง',
  error: 'ผิดพลาด (ไม่นำเข้า)',
  review: 'นำเข้าแต่ต้องตรวจสอบ',
  duplicateInFile: 'ซ้ำในไฟล์เดียวกัน',
};

const COMPLETENESS_LABELS: Record<string, string> = {
  complete: 'ครบถ้วน',
  incomplete: 'ยังไม่สมบูรณ์',
  needs_review: 'ต้องตรวจสอบ',
};

const BATCH_STATUS_LABELS: Record<string, string> = {
  draft: 'ร่าง (ยังไม่ยืนยัน)',
  confirmed: 'นำเข้าแล้ว',
  failed: 'ล้มเหลว',
  rolled_back: 'ย้อนกลับแล้ว',
};

export const importPage: Page = {
  title: 'นำเข้าและซิงค์ข้อมูล',
  render: () =>
    pageShell(
      'นำเข้าและซิงค์ข้อมูล',
      `<section class="card">
        <h3>1) เลือกไฟล์และชุดข้อมูล</h3>
        <p class="foot-note">รองรับไฟล์ CSV (ถ้าเป็น Excel ให้ Save as CSV ก่อน) — ไฟล์เดิมนำเข้าซ้ำจะอัปเดตรายการเดิม ไม่บวกยอดซ้ำ</p>
        <form id="file-form" class="grid-form">
          <label>ไฟล์ CSV<input type="file" name="file" accept=".csv,text/csv" required></label>
          <label>ชุดข้อมูล<select name="preset">${IMPORT_PRESETS.map(
            (p) => `<option value="${p.key}">${esc(p.label)}</option>`,
          ).join('')}</select></label>
          <label>ช่องทาง<select name="channel">${CHANNELS.map(
            ([v, l]) => `<option value="${v}">${esc(l)}</option>`,
          ).join('')}</select></label>
          <label>ปีของงวด (ถ้าระบุ แถวที่ปีไม่ตรงจะขึ้นให้ตรวจสอบ)<input name="expectedYear" type="number" placeholder="2025"></label>
          <div class="form-actions"><button type="submit">อ่านไฟล์</button><span id="file-msg" class="muted"></span></div>
        </form>
      </section>
      <section class="card" id="map-card" hidden>
        <h3>2) จับคู่คอลัมน์</h3>
        <div id="sample"></div>
        <form id="map-form" class="grid-form"></form>
        <div class="form-actions">
          <button type="button" id="btn-preview">ตรวจก่อนนำเข้า</button>
          <span id="preview-msg" class="muted"></span>
        </div>
      </section>
      <section class="card" id="preview-card" hidden>
        <h3>3) ผลการตรวจ</h3>
        <div id="preview-result"></div>
        <div class="form-actions">
          <button type="button" id="btn-confirm">ยืนยันนำเข้า</button>
          <span id="confirm-msg" class="muted"></span>
        </div>
      </section>
      <section class="card">
        <h3>ประวัติการนำเข้า</h3>
        <div id="batch-list">กำลังโหลด…</div>
      </section>
      <section class="card" id="tiktok-card">
        <h3>ซิงค์ TikTok Shop</h3>
        <p class="foot-note">ดึงสินค้า · ออเดอร์ · รอบเงินโอน · การคืน — กด "🔄 ซิงค์ข้อมูล" เพื่อดึงทันทีหรือลองใหม่เมื่องานล่าสุดล้มเหลว</p>
        <div id="tiktok-body" class="muted">กำลังโหลด…</div>
      </section>
      <section class="card">
        <h3>สถานะการซิงค์และความครบถ้วนของข้อมูล</h3>
        <div class="form-actions" style="margin-bottom:8px">
          <button type="button" id="btn-refresh-sync" class="mini">↻ รีเฟรชสถานะ</button>
          <span id="sync-msg" class="muted"></span>
        </div>
        <p class="foot-note">TikTok Shop ซิงค์อัตโนมัติทุกชั่วโมงเมื่อเชื่อมร้านแล้ว — ช่องทางอื่นใช้การนำเข้าไฟล์</p>
        <div id="sync-status">กำลังโหลด…</div>
      </section>
      ${drawerHtml}`,
    ),
  mount(root) {
    if (!getTenant()) return;
    const el = (id: string): HTMLElement => root.querySelector<HTMLElement>(`#${id}`)!;

    // สถานะของ flow ปัจจุบัน
    let csvText = '';
    let headers: string[] = [];
    let presetKey = IMPORT_PRESETS[0]!.key;
    let channel = '';
    let expectedYear: number | undefined;
    let filename = '';
    let previewBatchId = '';

    const requestBody = (): Row => {
      const preset = presetByKey(presetKey)!;
      const mapForm = root.querySelector<HTMLFormElement>('#map-form')!;
      const mapping: Record<string, string> = {};
      for (const field of preset.fields) {
        const v = val(mapForm, field.target);
        if (v) mapping[field.target] = v;
      }
      return {
        source: 'csv',
        ...(channel ? { channel } : {}),
        dataType: preset.key,
        csvText,
        mapping,
        config: presetToConfig(preset, mapping, expectedYear),
        classification: preset.classification,
        ...(filename ? { filename } : {}),
      };
    };

    const renderMapping = (): void => {
      const preset = presetByKey(presetKey)!;
      const guess = guessMapping(preset, headers);
      const options = (selected: string | undefined): string =>
        `<option value="">— ไม่ใช้ —</option>` +
        headers
          .map((h) => `<option value="${esc(h)}"${h === selected ? ' selected' : ''}>${esc(h)}</option>`)
          .join('');
      el('map-form').innerHTML = preset.fields
        .map(
          (f) =>
            `<label>${esc(f.label)}${f.required ? ' <span class="error">*</span>' : ''}
              <select name="${f.target}">${options(guess[f.target])}</select></label>`,
        )
        .join('');
      el('map-card').hidden = false;
    };

    const renderPreview = (res: api.ImportPreviewResult, applied: boolean): void => {
      const c = res.plan.counts;
      const cards = [
        ['รายการใหม่', c.new, ''],
        ['อัปเดตของเดิม', c.updated, ''],
        ['ไม่เปลี่ยนแปลง', c.unchanged, ''],
        ['ผิดพลาด (ไม่นำเข้า)', c.error, c.error > 0 ? 'value-warn' : ''],
        ['ต้องตรวจสอบ', c.review, c.review > 0 ? 'value-warn' : ''],
      ] as const;
      // รวมแถวที่มีปัญหา พร้อมสถานะที่จะเกิดขึ้นจริง
      const labelled: { rec: api.ImportRecord; status: string }[] = [
        ...res.plan.errors.map((rec) => ({ rec, status: 'error' })),
        ...res.plan.review.map((rec) => ({ rec, status: 'review' })),
        ...res.plan.duplicatesInFile.map((rec) => ({ rec, status: 'duplicateInFile' })),
      ];
      el('preview-result').innerHTML =
        `<div class="kpis">${cards
          .map(
            ([label, n, cls]) =>
              `<div class="kpi"><div class="label">${esc(label)}</div><div class="value ${cls}">${n}</div></div>`,
          )
          .join('')}</div>` +
        (res.duplicatePeriod
          ? `<div class="banner"><span>⚠️</span><div><strong>ข้อมูลชุดนี้เหมือนที่เคยนำเข้าแล้ว</strong> — ตรวจว่าไม่ใช่ไฟล์เดือนซ้ำก่อนยืนยัน (ระบบจะอัปเดตของเดิม ไม่บวกยอดซ้ำ)</div></div>`
          : '') +
        `<p class="muted">ช่วงข้อมูล: ${esc(res.coverage.from ?? '—')} ถึง ${esc(res.coverage.to ?? '—')} · ${res.coverage.rowCount} แถว · ความครบถ้วน: ${esc(
          COMPLETENESS_LABELS[res.coverage.completeness] ?? res.coverage.completeness,
        )}${applied ? ` · <strong>นำเข้าแล้ว ${res.appliedCount ?? 0} รายการ</strong>` : ''}</p>` +
        (labelled.length > 0
          ? `<h4>แถวที่ต้องดู (${labelled.length})</h4>` +
            dataTable(
              ['แถวที่', 'เลขที่รายการ', 'สถานะ', 'ปัญหา'],
              labelled.slice(0, 50).map(({ rec, status }) => ({
                cls: status === 'error' ? 'row-danger' : 'row-warn',
                cells: [
                  esc(rec.rowIndex),
                  esc(rec.externalId || '—'),
                  esc(STATUS_LABELS[status] ?? status),
                  rec.issues.length > 0
                    ? rec.issues
                        .map(
                          (i) =>
                            `${i.level === 'error' ? '⛔' : '⚠️'} ${esc(i.field ?? '')} ${esc(i.message)}`,
                        )
                        .join('<br>')
                    : 'เลขที่รายการซ้ำในไฟล์เดียวกัน',
                ],
              })),
            )
          : '<p class="muted">ไม่พบแถวที่มีปัญหา ✓</p>');
      el('preview-card').hidden = false;
      root.querySelector<HTMLButtonElement>('#btn-confirm')!.disabled = applied;
    };

    const loadBatches = async (): Promise<void> => {
      try {
        const { items } = await api.listBatches();
        el('batch-list').innerHTML = dataTable(
          ['เวลา', 'ไฟล์', 'ชุดข้อมูล', 'ช่องทาง', 'สถานะ', 'ใหม่', 'อัปเดต', 'ผิดพลาด', 'ตรวจสอบ', ''],
          items.map((b: Row) => ({
            cells: [
              esc(String(b['created_at'] ?? '').replace('T', ' ').slice(0, 16)),
              esc(b['filename'] ?? '—'),
              esc(b['data_type'] ?? '—'),
              esc(b['channel_code'] ?? '—'),
              b['status'] === 'confirmed'
                ? pill(BATCH_STATUS_LABELS['confirmed']!, 'ok')
                : b['status'] === 'rolled_back'
                  ? pill(BATCH_STATUS_LABELS['rolled_back']!, 'warn')
                  : pill(BATCH_STATUS_LABELS[String(b['status'])] ?? String(b['status'])),
              esc(b['new_count'] ?? 0),
              esc(b['updated_count'] ?? 0),
              esc(b['error_count'] ?? 0),
              esc(b['review_count'] ?? 0),
              b['status'] === 'confirmed'
                ? `<button class="mini" data-rollback="${esc(b['id'])}">ย้อนกลับ</button>`
                : '',
            ],
          })),
          'ยังไม่มีประวัติการนำเข้า',
        );
        el('batch-list')
          .querySelectorAll<HTMLButtonElement>('button[data-rollback]')
          .forEach((b) =>
            b.addEventListener('click', () => {
              void (async () => {
                const reason = await promptDialog({
                  title: 'ย้อนการนำเข้า',
                  label: 'เหตุผลการย้อน (บันทึกใน audit)',
                  confirmText: 'ย้อนการนำเข้า',
                });
                if (!reason) return;
                api
                  .importRollback(b.dataset['rollback']!, reason)
                  .then(loadBatches)
                  .catch((e: unknown) =>
                    showDrawer(root, 'ย้อนการนำเข้าไม่สำเร็จ', errorBox(e)),
                  );
              })();
            }),
          );
      } catch (e) {
        el('batch-list').innerHTML = errorBox(e);
      }
    };

    const loadSync = async (): Promise<void> => {
      try {
        const { jobs, coverage } = await api.syncStatus();
        el('sync-status').innerHTML =
          '<h4>งานซิงค์ล่าสุด</h4>' +
          dataTable(
            ['เวลาเริ่ม', 'ช่องทาง', 'ประเภทงาน', 'สถานะ', 'ครั้งที่', 'ข้อผิดพลาด'],
            jobs.map((j: Row) => ({
              cls: j['status'] === 'failed' ? 'row-danger' : undefined,
              cells: [
                esc(String(j['started_at'] ?? j['created_at'] ?? '').replace('T', ' ').slice(0, 16)),
                esc(j['channel_code'] ?? '—'),
                esc(j['job_type']),
                esc(j['status']),
                esc(j['attempt'] ?? 0),
                esc(j['error_message'] ?? '—'),
              ],
            })),
            'ยังไม่มีงานซิงค์ (TikTok Shop จะเริ่มเมื่อเชื่อมร้านแล้ว)',
          ) +
          '<h4>ความครบถ้วนของข้อมูลแต่ละแหล่ง</h4>' +
          dataTable(
            ['ช่องทาง', 'ชุดข้อมูล', 'ช่วงข้อมูล', 'ซิงค์ล่าสุด', 'ความครบถ้วน', 'ข้อผิดพลาด'],
            coverage.map((c: Row) => ({
              cls: c['completeness'] === 'complete' ? undefined : 'row-warn',
              cells: [
                esc(c['channel_code'] ?? '—'),
                esc(c['data_type']),
                `${esc(String(c['coverage_from'] ?? '—').slice(0, 10))} → ${esc(String(c['coverage_to'] ?? '—').slice(0, 10))}`,
                esc(String(c['last_synced_at'] ?? '—').replace('T', ' ').slice(0, 16)),
                c['completeness'] === 'complete'
                  ? pill('ครบถ้วน', 'ok')
                  : pill('ยังไม่สมบูรณ์', 'warn'),
                esc(c['error_count'] ?? 0),
              ],
            })),
            'ยังไม่มีข้อมูล coverage',
          );
      } catch (e) {
        el('sync-status').innerHTML = errorBox(e);
      }
    };

    bindSubmit(
      root.querySelector<HTMLFormElement>('#file-form')!,
      el('file-msg'),
      async (f) => {
        const input = f.elements.namedItem('file') as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) throw new Error('เลือกไฟล์ CSV ก่อน');
        filename = file.name;
        csvText = await file.text();
        const parsed = parseCsvPreview(csvText);
        if (parsed.headers.length === 0) throw new Error('ไฟล์ว่างหรืออ่านหัวตารางไม่ได้');
        headers = parsed.headers;
        presetKey = val(f, 'preset');
        channel = val(f, 'channel');
        const year = opt(f, 'expectedYear');
        expectedYear = year ? Number(year) : undefined;
        el('sample').innerHTML =
          `<p class="muted">ไฟล์ ${esc(filename)} · ${headers.length} คอลัมน์ (แสดงตัวอย่าง 5 แถวแรก)</p>` +
          dataTable(
            headers,
            parsed.rows.map((r) => ({ cells: r.map((c) => esc(c)) })),
          );
        renderMapping();
        el('preview-card').hidden = true;
        el('file-msg').textContent = 'อ่านไฟล์แล้ว ✓';
      },
      undefined,
      { reset: false, quiet: true },
    );

    root.querySelector<HTMLButtonElement>('#btn-preview')!.addEventListener('click', () => {
      const msg = el('preview-msg');
      msg.className = 'muted';
      msg.textContent = 'กำลังตรวจ…';
      void api
        .importPreview(requestBody())
        .then((res) => {
          previewBatchId = res.batchId;
          msg.textContent = 'ตรวจแล้ว — ดูผลด้านล่างก่อนยืนยัน';
          renderPreview(res, false);
          void loadBatches();
        })
        .catch((e: unknown) => {
          msg.className = 'error';
          msg.textContent = e instanceof Error ? e.message : 'ตรวจไม่สำเร็จ';
        });
    });

    root.querySelector<HTMLButtonElement>('#btn-confirm')!.addEventListener('click', () => {
      const msg = el('confirm-msg');
      if (!previewBatchId) {
        msg.className = 'error';
        msg.textContent = 'ต้องกด “ตรวจก่อนนำเข้า” ก่อน';
        return;
      }
      msg.className = 'muted';
      msg.textContent = 'กำลังนำเข้า…';
      void api
        .importConfirm({ ...requestBody(), batchId: previewBatchId })
        .then((res) => {
          msg.textContent = `นำเข้าแล้ว ✓ (${res.appliedCount ?? 0} รายการ)`;
          renderPreview(res, true);
          void loadBatches();
        })
        .catch((e: unknown) => {
          msg.className = 'error';
          msg.textContent = e instanceof Error ? e.message : 'นำเข้าไม่สำเร็จ';
        });
    });

    // แผงซิงค์ TikTok (retry) — หลังซิงค์สำเร็จ รีเฟรช job/coverage อัตโนมัติ
    void mountTikTok(el('tiktok-body'), () => {
      void loadSync();
    });

    root.querySelector<HTMLButtonElement>('#btn-refresh-sync')!.addEventListener('click', () => {
      const msg = el('sync-msg');
      msg.textContent = 'กำลังรีเฟรช…';
      void loadSync().then(() => {
        msg.textContent = 'อัปเดตแล้ว ✓';
      });
    });

    void loadBatches();
    void loadSync();
  },
};
